import React, { useRef, useState } from "react";
import Select, { components } from "react-select";
import { useSearchParams } from "react-router-dom";
import Search from "../../components/Header/Search/Search";
import Pagination from "../../components/Pagination/Pagination";
import EmptyState from "../../components/EmptyState";
import userRoleAuth from "../../utils/userRoleAuth/userRoleAuth";
import userRoles from "../../config/userRoles";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useGetLocationList,
  useGetDepartmentList,
  useGetDesignationList,
} from "../../queries/employeeQueries";
import PageLoader from "../../components/PageLoader";
import EmployeeGenereratedPayrollData from "./EmployeeGenereratedPayrollData";
import {
  useGetEmployeePolicyPayrollList,
  usePublishEmployeePayrollPolicy,
} from "../../queries/payrollQuries";
import { PAYROLL_STATUS } from "../../../src/redux/constants/payrollComponents";
import { useNavigate } from "react-router-dom";
const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <span className="ico-down text-blue-600"></span>
  </components.DropdownIndicator>
);

const EmployeePayrollGeneration = () => {
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get("month");
  const yearParam = searchParams.get("year");
  const navigate = useNavigate();
  const selectedMonth = monthParam ? Number(monthParam) : null;
  const selectedYear = yearParam ? Number(yearParam) : null;
  const user = useSelector((state) => state.user.auth);
  const isHr = userRoleAuth(user, userRoles.HR);

  const searchRef = useRef();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [searchFilter, setFilter] = useState({
    payroll_status: PAYROLL_STATUS.GENERATED,
    page_no: 1,
    page_size: 10,
    filter: "",
    department: "",
    designation: "",
    office: "",
  });

  const { data: payrollData, isLoading } = useGetEmployeePolicyPayrollList({
    ...searchFilter,
    month: selectedMonth || "",
    year: selectedYear || "",
  });
  const employeeList = Array.isArray(payrollData?.data) ? payrollData.data : [];
  const totalRecords = Number(payrollData?.totalRecords) || 0;
  const totalPages = Number(payrollData?.totalPages) || 1;

  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);

  const isFiltered =
    searchFilter.filter ||
    searchFilter.department ||
    searchFilter.designation ||
    searchFilter.office;

  const handleClearFilters = () => {
    setFilter({
      payroll_status: PAYROLL_STATUS.GENERATED,
      page_no: 1,
      page_size: 10,
      filter: "",
      department: "",
      designation: "",
      office: "",
    });

    setSelectedDesignation(null);
    setSelectedDepartment(null);
    setSelectedOffice(null);
    if (searchRef.current) searchRef.current.value = "";
  };

  const defaultLocationOption = { location_name: "Office: All", id: null };
  const defaultDesignationOption = {
    designation_title: "Designation: All",
    id: null,
  };
  const defaultDepartmentOption = {
    department_name: "Department: All",
    id: null,
  };

  const { data: getLocationList } = useGetLocationList();
  const { data: getDepartmentList } = useGetDepartmentList();
  const { data: getDesignationList } = useGetDesignationList();
  const generatePayrollMutation = usePublishEmployeePayrollPolicy();

  const processPayroll = async () => {
    setIsProcessing(true);
    setShowConfirmModal(false);
    setShowProgressModal(true);
    setProgress(0);
    setCancelProcessing(false);

    const selectedEmpDetails = employeeList?.filter((emp) =>
      selectedEmployees.includes(emp?.employee_id)
    );

    let processed = 0;
    let success = 0;
    let failed = 0;

    await selectedEmpDetails.reduce(async (prev, emp) => {
      await prev;

      if (cancelProcessing) return;

      if (!emp.payroll_policy_id) {
        processed++;
        setProgress(processed);
        return;
      }

      const payload = {
        payroll_policy_id: emp?.payroll_policy_id,
        employees: [{ employee_id: emp?.employee_id }],
           month: selectedMonth,
        year: selectedYear,
      };
      try {
        await generatePayrollMutation.mutateAsync(payload);
        success++;
      } catch (err) {
        failed++;
      }

      processed++;
      setProgress(processed);
    }, Promise.resolve());

    setSuccessCount(success);
    setFailureCount(failed);
    toast.success("Payroll publishing completed");

    setTimeout(() => {
      setShowProgressModal(false);
      setIsProcessing(false);
      setShowSuccessModal(true);
    }, 2000);
  };

  const handleGenerate = () => {
    if (!selectedEmployees.length) {
      toast.warn("No employees selected");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleCancelProcessing = () => {
    setCancelProcessing(true);
    toast.info("Cancelling remaining processing...");
  };

  const departmentList = Array.isArray(getDepartmentList?.result)
    ? [defaultDepartmentOption, ...getDepartmentList.result]
    : [];

  const designationList = Array.isArray(getDesignationList?.result)
    ? [defaultDesignationOption, ...getDesignationList.result]
    : [];

  const locationList = Array.isArray(getLocationList?.result)
    ? [defaultLocationOption, ...getLocationList.result]
    : [];

  const handleSetFilter = (value, source) => {
    setFilter((prev) => ({
      ...prev,
      [source]: value,
    }));
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="px-10 py-6">
        <div className="flex justify-between items-center pb-4">
          <div>
            <h2 className="text-lg font-semibold">Generate and Publish</h2>
            <p className="text-sm text-gray-500">
              View generated employee payroll and publish
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-3 items-center">
            <Select
              value={selectedDesignation}
              onChange={(selected) => {
                setSelectedDesignation(selected);
                handleSetFilter(selected?.value, "designation");
              }}
              placeholder={"Designation: All"}
              options={designationList.map((i) => ({
                label: i.designation_title,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />
            <Select
              value={selectedDepartment}
              onChange={(selected) => {
                setSelectedDepartment(selected);
                handleSetFilter(selected?.value, "department");
              }}
              placeholder={"Department: All"}
              options={departmentList.map((i) => ({
                label: i.department_name,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />
            <Select
              value={selectedOffice}
              onChange={(selected) => {
                setSelectedOffice(selected);
                handleSetFilter(selected?.value, "office");
              }}
              placeholder={"Office: All"}
              options={locationList.map((i) => ({
                label: i.location_name,
                value: i.id,
              }))}
              components={{ DropdownIndicator }}
              className="custom-select-container"
            />

            {isFiltered && (
              <span
                className="text-blue-800 font-medium cursor-pointer"
                onClick={handleClearFilters}
              >
                Clear Filters
              </span>
            )}
          </div>

          <Search
            eleRef={searchRef}
            onChange={(e) => handleSetFilter(e, "filter")}
            value={searchFilter.filter}
          />
        </div>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : employeeList.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <EmployeeGenereratedPayrollData
            employeeList={employeeList}
            selectedEmployees={selectedEmployees}
            setSelectedEmployees={setSelectedEmployees}
            month={selectedMonth}
            year={selectedYear}
            handleDownloadPayslip={() => {}}
          />

          <div className="flex justify-end gap-4 mt-6 px-10">
              <button
              className="btn btn--border text-sm font-medium"
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              className="btn text-sm font-medium"
              onClick={handleGenerate}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Publish Bulk Payroll"}
            </button>
          </div>

          {isProcessing && (
            <div className="w-full px-10 mt-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Processing... {progress} of{" "}
                {
                  Object.entries(
                    employeeList.reduce((acc, emp) => {
                      if (selectedEmployees.includes(emp.employee_id)) {
                        acc[emp.payroll_policy_id] = true;
                      }
                      return acc;
                    }, {})
                  ).length
                }{" "}
                policy batches completed
              </div>
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-600 h-4 transition-all duration-300 ease-out"
                  style={{
                    width: `${
                      (progress /
                        Object.entries(
                          employeeList.reduce((acc, emp) => {
                            if (selectedEmployees.includes(emp.employee_id)) {
                              acc[emp.payroll_policy_id] = true;
                            }
                            return acc;
                          }, {})
                        ).length) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="mt-[65px]">
            <Pagination
              page={{
                pageNo: searchFilter.page_no,
                setPageNo: (val) => handleSetFilter(val, "page_no"),
                pageSize: searchFilter.page_size,
                setPageSize: (val) => handleSetFilter(val, "page_size"),
                totalRecords: totalRecords,
                totalPages: totalPages,
              }}
            />
          </div>
          {showConfirmModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                <h4 className="text-md font-semibold text-gray-800 mb-4">
                  Publish Bulk Payroll
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to publish Bulk payroll for{" "}
                  {selectedEmployees.length} selected employees?
                  <span className="block text-red-600 font-semibold mt-2">
                    WARNING: This action cannot be undone.
                  </span>
                  <span className="block mt-1 text-red-600">
                    Once published:
                  </span>
                  <ul className="list-disc pl-6 mt-2 text-gray-600">
                    <li>Payroll data cannot be modified</li>
                    <li>Payslips will be viewable by employees</li>
                  </ul>
                </p>

                <div className="flex gap-4 justify-end">
                  <button
                    className="btn btn--border w-full"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn w-full" onClick={processPayroll}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {showProgressModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-semibold text-gray-800">
                    Processing Payroll
                  </h4>
                  <button
                    onClick={handleCancelProcessing}
                    className="text-sm text-red-500 font-medium hover:underline"
                  >
                    Cancel
                  </button>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  Processed {progress} of {selectedEmployees.length}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-blue-600 h-4 transition-all duration-300"
                    style={{
                      width: `${(progress / selectedEmployees.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {showSuccessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-[400px] text-center">
                <h2 className="text-lg font-semibold text-green-600 mb-4">
                  Payroll Generation Summary
                </h2>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 font-semibold text-gray-700">
                          Metric
                        </th>
                        <th className="px-4 py-2 font-semibold text-gray-700">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-4 py-2">Total Selected Employees</td>
                        <td className="px-4 py-2">
                          {selectedEmployees.length}
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-4 py-2">Success</td>
                        <td className="px-4 py-2">{successCount}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-4 py-2">Failed</td>
                        <td className="px-4 py-2">{failureCount}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  className="btn w-full mt-6"
                  onClick={() => {
                    setShowSuccessModal(false);
                    setSelectedEmployees([]);
                    setSuccessCount(0);
                    setFailureCount(0);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeePayrollGeneration;
